import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * True when content laid out on a single line would be wider than its container.
 * A zero-width container (not yet laid out, or `display: none`) never collapses.
 */
export function shouldCollapse(containerWidth: number, contentWidth: number): boolean {
  return containerWidth > 0 && contentWidth > containerWidth;
}

export type UseOverflowCollapseResult<C extends HTMLElement, G extends HTMLElement> = {
  /** Attach to the element whose available width is the limit. */
  containerRef: RefObject<C>;
  /** Attach to a hidden, single-line copy of the full content. */
  contentRef: RefObject<G>;
  /** Whether the single-line content overflows the container. Always false when `enabled` is false. */
  isOverflowing: boolean;
};

/**
 * Measures a hidden single-line copy of some content against its container with a
 * `ResizeObserver` and reports whether it overflows. Re-measures on any resize of
 * either element (viewport changes, font swaps, label changes).
 *
 * Renders as not overflowing on the server and on the first client render; the
 * measurement runs in a layout effect so client-side navigations settle before paint.
 */
export function useOverflowCollapse<C extends HTMLElement, G extends HTMLElement>(
  enabled: boolean,
): UseOverflowCollapseResult<C, G> {
  const containerRef = useRef<C>(null);
  const contentRef = useRef<G>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!enabled || !container || !content) {
      setIsOverflowing(false);
      return undefined;
    }

    const measure = () => {
      setIsOverflowing(shouldCollapse(container.getBoundingClientRect().width, content.getBoundingClientRect().width));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [enabled]);

  return { containerRef, contentRef, isOverflowing: enabled && isOverflowing };
}
